import { userModelToUserRef } from '$tablet-app/src/app/auth/UserModel';
import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import Dialog from '$tablet-app/src/app/components/Dialog';
import { DialogProps } from '$tablet-app/src/app/components/Dialog/Dialog';
import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import { useCurrentProfile } from '$tablet-app/src/app/users';
import { FeatureDomain } from '@akin-home/amplify';
import {
  useHabitatStickyNotes,
  useQuickUpdateStickyNote,
} from '@akin-home/sticky-notes/react';
import React, { FC, useState } from 'react';
import styled from 'styled-components/native';
import {
  postDeleteRecordLogEvent,
  postUpdateRecordLogEvent,
} from '../../logging/functions';
import { postCreateRecordLogEvent } from '../../logging/functions/postCreateRecordLogEvent';
import { useListCurrentHabitatUsers } from '../../users';
import {
  checkUserIsNoteOwner,
  createStickyNote,
  filterStickyNotesForUser,
  setStickyNoteAcknowledgement,
} from '../functions';
import { StickyNote } from '../types';
import ManageStickyNoteGrid from './ManageStickyNoteGrid';
import StickyNoteEditTile from './StickyNoteEditTile';

interface StickyNoteDialogProps extends DialogProps {
  editingNote?: StickyNote;
  onEditClose: () => void;
}

const ManageStickyNoteDialog: FC<StickyNoteDialogProps> = (props) => {
  const { profile: user } = useCurrentProfile();

  const { data: users } = useListCurrentHabitatUsers();

  const {
    sticky_notes: notes,
    create,
    update,
    remove,
  } = useHabitatStickyNotes(user.habitatID);

  const sticky_notes = filterStickyNotesForUser(notes, user);

  const quickUpdate = useQuickUpdateStickyNote();

  const [editingNote, setEditingNote] = useState<StickyNote | undefined>(
    props.editingNote
  );

  const onPressAdd = () => {
    const newNote = createStickyNote({
      habitatID: user.habitatID,
      draft: true,
      owner: userModelToUserRef(user),
    });

    setEditingNote(newNote);
  };

  const onPressThumb = (sticky_note: StickyNote) => {
    quickUpdate({
      id: sticky_note.id,
      acknowledgements: setStickyNoteAcknowledgement(sticky_note, user.id)
        .acknowledgements,
    });
  };

  const onPressTile = (note: StickyNote) =>
    checkUserIsNoteOwner(note, user)
      ? setEditingNote(note)
      : onPressThumb(note);

  const onPressDelete = (id: string) => {
    remove(id).then(() => {
      postDeleteRecordLogEvent({
        user_id: user.id,
        feature_domain: FeatureDomain.STICKY_NOTES,
        data: {
          id,
          model: 'StickyNote',
        },
      });
    });

    if (props.editingNote) {
      props.onEditClose();
    }
  };

  const onSave = (note: StickyNote) => {
    if (note.draft) {
      create(note).then((result) => {
        postCreateRecordLogEvent({
          user_id: user.id,
          feature_domain: FeatureDomain.STICKY_NOTES,
          data: {
            id: result.data.createStickyNote?.id,
            model: 'StickyNote',
          },
        });
      });
    } else {
      update(note).then(() => {
        postUpdateRecordLogEvent({
          user_id: user.id,
          feature_domain: FeatureDomain.STICKY_NOTES,
          data: {
            id: note.id,
            model: 'StickyNote',
          },
        });
      });
    }

    if (props.editingNote) {
      props.onEditClose();
    }

    setEditingNote(undefined);
  };

  const onBack =
    !!editingNote && !props.editingNote
      ? () => setEditingNote(undefined)
      : undefined;

  const handleCloseNotes = () => props.onEditClose();

  return (
    <Dialog size="fullscreen" onClose={handleCloseNotes} {...props}>
      <HeaderRow>
        {onBack ? (
          <NewButton variant="secondary" icon="chevron-left" onPress={onBack} />
        ) : null}

        <Spacer flex={1} />

        <ManageNotesHeader justifyContent="center" alignItems="center">
          <Text preset="h3">Sticky Notes</Text>
        </ManageNotesHeader>

        <Spacer flex={1} />

        <NewButton
          variant="secondary"
          icon="times"
          onPress={handleCloseNotes}
        />
      </HeaderRow>

      {editingNote ? (
        <Row justifyContent="center" alignItems="center">
          <StickyNoteEditTile
            note={editingNote}
            onSave={onSave}
            onPressDelete={onPressDelete}
          />
        </Row>
      ) : (
        <GridContainer>
          <ManageStickyNoteGrid
            notes={sticky_notes}
            current_user={user}
            users={users}
            onPressAddNew={onPressAdd}
            onPress={onPressTile}
            onPressEdit={onPressTile}
            onDelete={onPressDelete}
            onSave={onSave}
            onPressAcknowledge={onPressThumb}
          />
        </GridContainer>
      )}
    </Dialog>
  );
};

const ManageNotesHeader = styled(Row)`
  margin-left: 5%;
  height: 35px;
  width: 87%;
  min-width: 87%;
`;

const HeaderRow = styled(Row)`
  justify-content: center;
  padding: 45px 53px 33px;
`;

const GridContainer = styled.View`
  flex: 1;
  padding: 0 55px 55px;
`;

export default ManageStickyNoteDialog;
